import axios from "axios";
import { useState, useContext, useEffect, useRef, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { NavigationContext } from "../context/NavigationContext";
import {
    Table,
    Typography,
    message,
    Spin,
    Tag,
    Modal,
    Input,
    Button,
    DatePicker,
    Row,
    Col,
    Card,
    Select,
    Timeline,
    Popover,
    Checkbox,
} from "antd";
import { CheckOutlined, CloseOutlined, SettingOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { DownloadOutlined } from "@ant-design/icons";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

// Fetch a large batch from the backend since status filtering now happens
// client-side (it depends on each record's approval_logs, not the outer status field)
const BACKEND_PAGE_SIZE = 5000;

// ---------------------------------------------------------------------------
// Helpers for reading approval_logs safely regardless of exactly which shape
// the backend serializer returns (order vs level_order, approver id nested
// under different keys, names with extra whitespace / different casing, etc).
// ---------------------------------------------------------------------------

// Normalize a name for comparison: collapse whitespace, strip common
// salutations, and lower-case everything.
const normalizeName = (name) => {
    if (!name) return "";
    return name
        .toString()
        .replace(/\b(mr|mrs|ms|miss|dr)\.?\s+/gi, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
};

// A single approval log's "order" can come back as `order` or `level_order`
// depending on the endpoint/serializer version. Read whichever is present.
const getLogOrder = (log) => log?.order ?? log?.level_order ?? null;

// Try every plausible place an approver id could live on a log entry.
const getLogApproverId = (log) =>
    log?.approver?.id ??
    log?.approver_id ??
    log?.approver?.user?.id ??
    log?.approver?.employee?.id ??
    null;

export default function OtApprovals({ is_employee_view = false, viewMode = "" }) {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    const [allowEdit, setAllowEdit] = useState(false);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [actionType, setActionType] = useState("");
    const [remarks, setRemarks] = useState("");

    // ✅ Notification highlight state
    const [targetOtId, setTargetOtId] = useState(null);
    const highlightRowRef = useRef(null);

    // Pagination state (now purely client-side, since status filtering happens client-side)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    // Filter states
    const [filters, setFilters] = useState({
        searchName: "",
        searchCode: "",
        dateFrom: "",
        dateTo: "",
        selectedStatus: "PENDING",
        selectedOrganisation: "",
        selectedDepartment: "",
        selectedDesignation: "",
    });

    // Dropdown lists
    const [organisationList, setOrganisationList] = useState([]);
    const [departmentList, setDepartmentList] = useState([]);
    const [designationList, setDesignationList] = useState([]);

    // ✅ Custom Columns feature state
    const ALL_COLUMN_OPTIONS = useMemo(() => {
        const options = [
            { key: "employee", label: "Employee" },
            { key: "code", label: "Code" },
            { key: "date", label: "Date" },
            { key: "in_time", label: "First In" },
            { key: "out_time", label: "Last Out" },
            { key: "ot_hours", label: "OT Hours" },
        ];
        if (authData?.employee?.is_superuser) {
            options.push({ key: "ot_amount", label: "OT Amount" });
        }
        options.push(
            { key: "employee_remarks", label: "Employee Remarks" },
            { key: "status", label: "Overall Status" },
            { key: "organisation", label: "Organisation" },
            { key: "department", label: "Department" },
            { key: "designation", label: "Designation" },
            { key: "approval_logs", label: "Approval Hierarchy" },
        );
        return options;
    }, [authData?.employee?.is_superuser]);

    const [visibleColumnKeys, setVisibleColumnKeys] = useState(
        ALL_COLUMN_OPTIONS.map((o) => o.key)
    );
    const [columnPickerOpen, setColumnPickerOpen] = useState(false);

    // Keep visible keys in sync if the available options change (e.g. superuser toggles OT Amount col in)
    useEffect(() => {
        setVisibleColumnKeys((prev) => {
            const allKeys = ALL_COLUMN_OPTIONS.map((o) => o.key);
            const kept = prev.filter((k) => allKeys.includes(k));
            return kept.length ? [...new Set([...kept])] : allKeys;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ALL_COLUMN_OPTIONS]);

    const handleColumnVisibilityChange = (checkedKeys) => {
        setVisibleColumnKeys(checkedKeys);
    };

    const handleSelectAllColumns = () => {
        setVisibleColumnKeys(ALL_COLUMN_OPTIONS.map((o) => o.key));
    };

    const handleResetColumns = () => {
        setVisibleColumnKeys(ALL_COLUMN_OPTIONS.map((o) => o.key));
    };

    // Get current user's full name
    const getCurrentUserName = () => {
        if (!authData?.employee?.user) return "";
        const firstName = authData.employee.user.first_name || "";
        const lastName = authData.employee.user.last_name || "";
        return `${firstName} ${lastName}`.trim();
    };

    // ✅ Find the approval_logs entry that belongs to the currently logged-in approver.
    // Matches by approver id first (checked across several possible shapes),
    // then falls back to a normalized (whitespace/case/salutation-insensitive)
    // name match, since some endpoints only return `approver_name` and no
    // nested `approver` object with an id at all.
    const getMyApprovalLog = (record) => {
        const logs = record?.approval_logs || [];
        if (!logs.length) return null;

        const myId = authData?.employee?.id;
        if (myId != null) {
            const byId = logs.find((log) => getLogApproverId(log) === myId);
            if (byId) return byId;
        }

        const myNameNormalized = normalizeName(getCurrentUserName());
        if (myNameNormalized) {
            const byName = logs.find(
                (log) => normalizeName(log.approver_name) === myNameNormalized
            );
            if (byName) return byName;
        }

        return null;
    };

    // Fetch Organisation List
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisationList(res.data.results);
        } catch (err) {
            console.log("Organisation fetch failed");
        }
    };

    // Fetch Departments by Org
    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDepartmentList(res.data.results);
        } catch (err) {
            console.log("Department fetch failed");
        }
    };

    // Fetch Designations by Dept
    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(
                `${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            setDesignationList(res.data.results);
        } catch (err) {
            console.log("Designation fetch failed");
        }
    };

    // Build query params from filters
    // NOTE: `status` is intentionally NOT sent to the backend anymore.
    // Status filtering is now based on each record's approval_logs (the logged-in
    // approver's own decision), which we compute client-side after fetching.
    const buildQueryParams = (page = 1, pageSize = 10, overrideFilters = {}) => {
        const mergedFilters = {
            searchName: "searchName" in overrideFilters ? overrideFilters.searchName : filters.searchName,
            searchCode: "searchCode" in overrideFilters ? overrideFilters.searchCode : filters.searchCode,
            dateFrom: "dateFrom" in overrideFilters ? overrideFilters.dateFrom : filters.dateFrom,
            dateTo: "dateTo" in overrideFilters ? overrideFilters.dateTo : filters.dateTo,
            selectedOrganisation: "selectedOrganisation" in overrideFilters ? overrideFilters.selectedOrganisation : filters.selectedOrganisation,
            selectedDepartment: "selectedDepartment" in overrideFilters ? overrideFilters.selectedDepartment : filters.selectedDepartment,
            selectedDesignation: "selectedDesignation" in overrideFilters ? overrideFilters.selectedDesignation : filters.selectedDesignation,
        };

        const params = new URLSearchParams();

        params.append('page', page);
        params.append('page_size', pageSize);

        if (is_employee_view) {
            params.append('is_employee_view', 'true');
        }
        if (viewMode === 'admin') {
            params.append('view_mode', 'admin');
        }

        if (mergedFilters.searchName.trim()) {
            params.append('employee_name', mergedFilters.searchName.trim());
        }

        if (mergedFilters.searchCode.trim()) {
            params.append('employee_code', mergedFilters.searchCode.trim());
        }

        if (mergedFilters.dateFrom) {
            params.append('from_date', mergedFilters.dateFrom);
        }

        if (mergedFilters.dateTo) {
            params.append('to_date', mergedFilters.dateTo);
        }

        if (mergedFilters.selectedOrganisation) {
            params.append('organisation', mergedFilters.selectedOrganisation);
        }

        if (mergedFilters.selectedDepartment) {
            params.append('department', mergedFilters.selectedDepartment);
        }

        if (mergedFilters.selectedDesignation) {
            params.append('designation', mergedFilters.selectedDesignation);
        }

        return params.toString();
    };

    // Fetch OT data (all records matching non-status filters; status is applied client-side)
    const fetchData = async (page = 1, pageSize = pagination.pageSize, overrideFilters = {}) => {
        setLoading(true);
        try {
            const queryString = buildQueryParams(1, BACKEND_PAGE_SIZE, overrideFilters);
            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${queryString}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const resultData = res.data.results || res.data;

            setData(resultData);

            // Reset to requested page for the (client-side) pagination
            setPagination((prev) => ({
                ...prev,
                current: page,
                pageSize: pageSize,
            }));
        } catch (err) {
            message.error("Failed to fetch OT requests");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (authData?.access) {
            fetchOrganisations();
        }
        if (authData?.employee?.role === 2) setAllowEdit(true);
    }, [authData]);

    // ✅ Consume navigation params from notification click
    useEffect(() => {
        if (navigationParams?.otId) {
            console.log("🎯 OtApprovals: Received navigation params:", navigationParams);
            setTargetOtId(navigationParams.otId);
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // Fetch data from backend when authentication is loaded
    useEffect(() => {
        if (authData?.access) {
            fetchData(1, pagination.pageSize);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authData?.access]);

    // ✅ Status-filtered data: based on the logged-in approver's own entry
    // inside each record's approval_logs, NOT the record's outer `status` field.
    //
    // DEBUG NOTE: if records are missing from the "Pending" tab that you
    // expect to see, open the browser console — the warning below tells you
    // exactly which records had no matching approval_logs entry for the
    // current user, which is the #1 cause of "API has the row, table doesn't".
    const statusFilteredData = useMemo(() => {
        if (!filters.selectedStatus) return data;

        const unmatched = [];
        const result = data.filter((record) => {
            const myLog = getMyApprovalLog(record);
            if (!myLog) {
                unmatched.push(record.id);
                return false;
            }
            return (myLog.status || "").toUpperCase() === filters.selectedStatus.toUpperCase();
        });

        if (unmatched.length) {
            console.warn(
                "[OtApprovals] No approval_logs entry found for current user " +
                `(id=${authData?.employee?.id}, name="${getCurrentUserName()}") on OT request id(s):`,
                unmatched,
                "— these records were hidden. If they should be visible, check that " +
                "approver_name / approver.id on the backend actually matches this user."
            );
        }

        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, filters.selectedStatus, authData?.employee?.id]);

    // ✅ Overall counts for Pending / Approved / Rejected, based on the same
    // per-user approval_logs lookup used for filtering — so the numbers on
    // the buttons always match what you'd see after clicking them.
    const statusCounts = useMemo(() => {
        const counts = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
        data.forEach((record) => {
            const myLog = getMyApprovalLog(record);
            if (!myLog) return;
            const status = (myLog.status || "").toUpperCase();
            if (status in counts) {
                counts[status] += 1;
            }
        });
        return counts;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, authData?.employee?.id]);

    // ✅ Client-side page slice of the status-filtered data
    const paginatedData = useMemo(() => {
        const start = (pagination.current - 1) * pagination.pageSize;
        return statusFilteredData.slice(start, start + pagination.pageSize);
    }, [statusFilteredData, pagination.current, pagination.pageSize]);

    // ✅ Scroll to highlighted row once it's present in the status-filtered data
    useEffect(() => {
        if (targetOtId && statusFilteredData.length > 0) {
            const targetIdNum = parseInt(targetOtId, 10);
            const found = statusFilteredData.find((r) => r.id === targetIdNum);
            if (found) {
                console.log("🎯 OtApprovals: Found target OT record:", found);
                setTimeout(() => {
                    if (highlightRowRef.current) {
                        highlightRowRef.current.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                        });
                    }
                }, 300);
            }
        }
    }, [targetOtId, statusFilteredData]);

    // Apply filters - fetch new data (non-status filters only)
    const applyFilters = () => {
        fetchData(1, pagination.pageSize);
    };

    // Handle table pagination change (client-side now — no backend call needed)
    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize,
        }));
    };

    // Export to Excel with all filtered data (status filter applied client-side, same as on-screen)
    const exportToExcel = async () => {
        try {
            const queryString = buildQueryParams(1, BACKEND_PAGE_SIZE, {});
            const res = await axios.get(
                `${backUrl}/api/payroll/manager/ot-requests/?${queryString}`,
                {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                }
            );

            const rawExportData = res.data.results || res.data;

            // Apply the same approval_logs-based status filter used on-screen
            const exportData = filters.selectedStatus
                ? rawExportData.filter((record) => {
                    const myLog = getMyApprovalLog(record);
                    if (!myLog) return false;
                    return (myLog.status || "").toUpperCase() === filters.selectedStatus.toUpperCase();
                })
                : rawExportData;

            if (!exportData.length) {
                message.warning("No data available to export");
                return;
            }

            // Sheet 1: Main OT Requests
            const mainSheetData = exportData.map((row) => ({
                Employee: row.employee_name || "—",
                Code: row.employee?.emp_code || row.employee?.username || "—",
                Organisation: row.employee?.organisation_name,
                Department: row.employee?.department_name,
                Designation: row.employee?.designation_name,
                Date: row.attendance_date,
                "OT Hours": row.requested_ot_hours,
                "OT Amount": row.ot_amount || "",
                "Employee Remarks": row.employee_remarks || "",
                Status: row.status?.toUpperCase(),
            }));

            const ws1 = XLSX.utils.json_to_sheet(mainSheetData);

            // Sheet 2: Approval Logs
            let approvalRows = [];

            exportData.forEach((record) => {
                (record.approval_logs || []).forEach((log) => {
                    approvalRows.push({
                        Employee: record.employee_name || "—",
                        Date: record.attendance_date,
                        Order: getLogOrder(log),
                        Approver: log.approver_name,
                        Status: log.status?.toUpperCase(),
                        Remarks: log.remarks || "",
                        Timestamp: log.timestamp
                            ? dayjs(log.timestamp).format("DD MMM YYYY, hh:mm A")
                            : "",
                    });
                });
            });

            const ws2 = XLSX.utils.json_to_sheet(approvalRows);

            // Create Workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws1, "OT Requests");
            XLSX.utils.book_append_sheet(wb, ws2, "Approval Logs");

            // Download Excel
            XLSX.writeFile(wb, "OT_Requests.xlsx");
        } catch (err) {
            message.error("Failed to export data");
        }
    };

    // Organisation Change
    const handleOrgChange = (value) => {
        setFilters(prev => ({
            ...prev,
            selectedOrganisation: value || "",
            selectedDepartment: "",
            selectedDesignation: "",
        }));
        setDepartmentList([]);
        setDesignationList([]);
        if (value) {
            fetchDepartments(value);
        }
        fetchData(1, pagination.pageSize, {
            selectedOrganisation: value || "",
            selectedDepartment: "",
            selectedDesignation: "",
        });
    };

    // Department Change
    const handleDeptChange = (value) => {
        setFilters(prev => ({
            ...prev,
            selectedDepartment: value || "",
            selectedDesignation: "",
        }));
        setDesignationList([]);
        if (value) {
            fetchDesignations(value);
        }
        fetchData(1, pagination.pageSize, {
            selectedDepartment: value || "",
            selectedDesignation: "",
        });
    };

    // Designation Change
    const handleDesigChange = (value) => {
        setFilters(prev => ({
            ...prev,
            selectedDesignation: value || "",
        }));
        fetchData(1, pagination.pageSize, {
            selectedDesignation: value || "",
        });
    };

    // Search Name Change
    const handleSearchNameChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, searchName: val }));
        if (!val.trim()) {
            fetchData(1, pagination.pageSize, { searchName: "" });
        }
    };

    // Search Code Change
    const handleSearchCodeChange = (e) => {
        const val = e.target.value;
        setFilters(prev => ({ ...prev, searchCode: val }));
        if (!val.trim()) {
            fetchData(1, pagination.pageSize, { searchCode: "" });
        }
    };

    // ✅ Status button handler — purely client-side now (filters records by the
    // logged-in approver's own approval_logs status). No backend re-fetch needed.
    const handleStatusFilter = (status) => {
        const nextStatus = filters.selectedStatus === status ? "" : status;
        setFilters(prev => ({ ...prev, selectedStatus: nextStatus }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Date range change
    const handleDateRangeChange = (dates, dateStrings) => {
        const fromDate = dateStrings ? dateStrings[0] : "";
        const toDate = dateStrings ? dateStrings[1] : "";
        setFilters(prev => ({
            ...prev,
            dateFrom: fromDate,
            dateTo: toDate,
        }));
        fetchData(1, pagination.pageSize, {
            dateFrom: fromDate,
            dateTo: toDate,
        });
    };

    // Clear all filters
    const clearFilters = () => {
        const cleared = {
            searchName: "",
            searchCode: "",
            dateFrom: "",
            dateTo: "",
            selectedStatus: "",
            selectedOrganisation: "",
            selectedDepartment: "",
            selectedDesignation: "",
        };
        setFilters(cleared);
        setDepartmentList([]);
        setDesignationList([]);
        fetchData(1, pagination.pageSize, cleared);
    };

    // Open Modal
    const openModal = (record, type) => {
        setSelectedRecord(record);
        setActionType(type);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit Approve / Reject
    const handleSubmit = async () => {
        if (!remarks.trim()) {
            message.warning("Please enter manager remarks");
            return;
        }

        let payload = { remarks: remarks };
        if (allowEdit) {
            if (actionType === "approve") {
                if (!selectedRecord.ot_amount || Number(selectedRecord.ot_amount) <= 0) {
                    message.warning("Please enter valid OT Amount before approving");
                    return;
                }

                payload.ot_amount = selectedRecord.ot_amount;
            }
        }

        const endpoint =
            actionType === "approve"
                ? `${backUrl}/api/payroll/manager/ot-requests/${selectedRecord.id}/approve/`
                : `${backUrl}/api/payroll/manager/ot-requests/${selectedRecord.id}/reject/`;

        setSubmitLoading(true);
        try {
            await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            toast.success(
                `OT request ${actionType === "approve" ? "approved" : "rejected"} successfully`
            );

            setIsModalVisible(false);
            fetchData(pagination.current, pagination.pageSize);
        } catch (err) {
            const errorMessage =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                "Failed to process OT request";

            toast.error(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    // ✅ All possible columns, each tagged with a `key` so they can be toggled
    const allColumns = [
        {
            title: "Employee",
            key: "employee",
            align: "center",
            render: (_, record) => record.employee_name || "—",
        },
        {
            title: "Code",
            key: "code",
            align: "center",
            render: (_, record) => record.employee?.emp_code || record.employee?.username || "—",
        },

        {
            title: "Date",
            key: "date",
            dataIndex: "attendance_date",
            align: "center",
        },
        {
            title: "First In",
            key: "in_time",
            dataIndex: "in_time",
            align: "center",
        },
        {
            title: "Last Out",
            key: "out_time",
            dataIndex: "out_time",
            align: "center",
        },
        {
            title: "OT Hours",
            key: "ot_hours",
            dataIndex: "approved_ot_hours",
            align: "center",
        },
        ...(authData?.employee?.is_superuser
            ? [
                {
                    title: "OT Amount",
                    key: "ot_amount",
                    dataIndex: "ot_amount",
                    align: "center",
                }
            ]
            : []),

        {
            title: "Employee Remarks",
            key: "employee_remarks",
            dataIndex: "employee_remarks",
            align: "center",
        },
        {
            title: "Overall Status",
            key: "status",
            dataIndex: "status",
            align: "center",
            render: (status) => {
                let color =
                    status === "approved"
                        ? "green"
                        : status === "rejected"
                            ? "red"
                            : "orange";
                return <p color={color}>{status?.toUpperCase()}</p>;
            },
        },
        {
            title: "Organisation",
            key: "organisation",
            dataIndex: ["employee", "organisation_name"],
            align: "center",
        },
        {
            title: "Department",
            key: "department",
            dataIndex: ["employee", "department_name"],
            align: "center",
        },
        {
            title: "Designation",
            key: "designation",
            dataIndex: ["employee", "designation_name"],
            align: "center",
        },
        {
            title: "Approval Hierarchy",
            key: "approval_logs",
            width: 450,
            render: (_, record) => {
                const logs = record.approval_logs || [];
                if (!logs.length) return "-";

                return (
                    <div style={{ marginTop: "16px" }}>
                        <Timeline
                            items={logs.map((log, index) => {
                                const isRejected = log.status === "REJECTED";
                                const isApproved = log.status === "APPROVED";
                                const timelineColor = isRejected ? "red" : isApproved ? "green" : "gray";

                                return {
                                    color: timelineColor,
                                    children: (
                                        <div style={{ marginBottom: "8px" }}>
                                            {/* Name and Status Tag */}
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                                                <Text strong style={{ fontSize: "15px" }}>
                                                    {log.approver_name ||
                                                        (log.approver?.first_name || log.approver?.last_name
                                                            ? `${log.approver.first_name || ""} ${log.approver.last_name || ""}`
                                                            : (log.approver?.user?.first_name || log.approver?.user?.last_name
                                                                ? `${log.approver.user.first_name || ""} ${log.approver.user.last_name || ""}`
                                                                : (log.approver?.user?.username || "-"))).trim()}
                                                </Text>
                                                <Tag color={isRejected ? "error" : isApproved ? "success" : "default"} style={{ margin: 0 }}>
                                                    {log.status}
                                                </Tag>
                                            </div>

                                            {/* Designation */}
                                            {log.approver?.designation_name && (
                                                <div style={{ color: "#8c8c8c", fontSize: "13px", marginTop: "2px" }}>
                                                    {log.approver.designation_name}
                                                </div>
                                            )}

                                            {/* Combined Action Details (Additive) */}
                                            <div style={{ marginTop: "6px" }}>
                                                {/* Regular Approval / Rejection Block */}
                                                {(log.updated_at || log.remarks) && (
                                                    <div style={{ marginBottom: "8px" }}>
                                                        {log.updated_at && (
                                                            <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                                <Text strong style={{ fontSize: "11px", color: "#595959" }}>{isRejected ? "Rejected On: " : "Approved On: "}</Text>
                                                                {dayjs(log.updated_at).format("DD MMM YYYY [at] hh:mm A")}
                                                            </div>
                                                        )}
                                                        {log.remarks && (
                                                            <div style={{ fontSize: "13px" }}>
                                                                <Text strong>{isRejected ? "Rejection Remark: " : "Remark: "}</Text>
                                                                <Text>{log.remarks}</Text>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ),
                                };
                            })}
                        />
                    </div>
                );
            },
        },
        {
            title: "Action",
            key: "action",
            align: "center",
            render: (record) =>
                allowEdit ? (
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Input
                            type="number"
                            placeholder="OT Amount"
                            style={{ width: "120px" }}
                            value={record.ot_amount || ""}
                            onChange={(e) => {
                                const val = e.target.value;
                                setData(prev => prev.map(item =>
                                    item.id === record.id ? { ...item, ot_amount: val } : item
                                ));
                            }}
                        />

                        <Button
                            shape="circle"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "green", color: "white" }}
                            onClick={() => openModal(record, "approve")}
                        />

                        <Button
                            shape="circle"
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => openModal(record, "reject")}
                        />
                    </div>
                ) : (
                    <div style={{ display: "flex", justifyContent: "center", gap: "12px" }}>
                        <Button
                            shape="circle"
                            icon={<CheckOutlined />}
                            style={{ backgroundColor: "green", color: "white" }}
                            onClick={() => openModal(record, "approve")}
                        />
                        <Button
                            shape="circle"
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => openModal(record, "reject")}
                        />
                    </div>
                ),
        },
    ];

    // ✅ Filter columns based on user's custom column selection.
    // "action" column is always shown since it's required for approving/rejecting requests.
    const columns = useMemo(
        () =>
            allColumns.filter(
                (col) => col.key === "action" || visibleColumnKeys.includes(col.key)
            ),
        [allColumns, visibleColumnKeys]
    );

    // ✅ Custom Columns picker content
    const columnPickerContent = (
        <div style={{ width: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleSelectAllColumns}>
                    Select All
                </Button>
                <Button type="link" size="small" style={{ padding: 0 }} onClick={handleResetColumns}>
                    Reset
                </Button>
            </div>
            <Checkbox.Group
                style={{ display: "flex", flexDirection: "column", gap: "6px" }}
                value={visibleColumnKeys}
                onChange={handleColumnVisibilityChange}
            >
                {ALL_COLUMN_OPTIONS.map((opt) => (
                    <Checkbox key={opt.key} value={opt.key}>
                        {opt.label}
                    </Checkbox>
                ))}
            </Checkbox.Group>
        </div>
    );

    const expandedRowRender = (record) => {
        const approvalColumns = [
            {
                title: "Order",
                key: "order",
                align: "center",
                render: (_, log) => getLogOrder(log) ?? "—",
            },
            {
                title: "Approver",
                dataIndex: "approver_name",
                align: "center",
            },
            {
                title: "Overall Status",
                dataIndex: "status",
                align: "center",
                render: (status) => {
                    let color =
                        status?.toLowerCase() === "approved"
                            ? "green"
                            : status?.toLowerCase() === "rejected"
                                ? "red"
                                : "orange";
                    return <Tag color={color}>{status?.toUpperCase()}</Tag>;
                },
            },
            {
                title: "Remarks",
                dataIndex: "remarks",
                align: "center",
                render: (text) => text || "—",
            },
            {
                title: "Timestamp",
                dataIndex: "timestamp",
                align: "center",
                render: (value) =>
                    value ? dayjs(value).format("DD MMM YYYY, hh:mm A") : "—",
            },
        ];

        return (
            <Table
                columns={approvalColumns}
                dataSource={record.approval_logs || []}
                rowKey="id"
                pagination={false}
                size="small"
                bordered
                style={{ marginLeft: "40px" }}
            />
        );
    };



    return (
        <div style={{ margin: "30px" }}>
            {/* STATUS FILTER BUTTONS */}
            <Row justify="center" gutter={16} style={{ marginBottom: 20 }}>
                <Col>
                    <Button
                        type={filters.selectedStatus === "PENDING" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("PENDING")}
                        style={{
                            backgroundColor: filters.selectedStatus === "PENDING" ? "#faad14" : undefined,
                            borderColor: filters.selectedStatus === "PENDING" ? "#faad14" : undefined,
                            color: filters.selectedStatus === "PENDING" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Pending ({statusCounts.PENDING})
                    </Button>
                </Col>
                <Col>
                    <Button
                        type={filters.selectedStatus === "APPROVED" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("APPROVED")}
                        style={{
                            backgroundColor: filters.selectedStatus === "APPROVED" ? "#52c41a" : undefined,
                            borderColor: filters.selectedStatus === "APPROVED" ? "#52c41a" : undefined,
                            color: filters.selectedStatus === "APPROVED" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Approved ({statusCounts.APPROVED})
                    </Button>
                </Col>
                <Col>
                    <Button
                        type={filters.selectedStatus === "REJECTED" ? "primary" : "default"}
                        onClick={() => handleStatusFilter("REJECTED")}
                        style={{
                            backgroundColor: filters.selectedStatus === "REJECTED" ? "#ff4d4f" : undefined,
                            borderColor: filters.selectedStatus === "REJECTED" ? "#ff4d4f" : undefined,
                            color: filters.selectedStatus === "REJECTED" ? "white" : undefined,
                            minWidth: "120px"
                        }}
                    >
                        Rejected ({statusCounts.REJECTED})
                    </Button>
                </Col>
            </Row>

            <Card style={{ marginBottom: 20, padding: 10 }}>
                <Row gutter={16}>
                    <Col span={8}>
                        <Input
                            placeholder="Search by Employee Name"
                            value={filters.searchName}
                            onChange={handleSearchNameChange}
                            onPressEnter={() => fetchData(1, pagination.pageSize)}
                            allowClear
                        />
                    </Col>

                    <Col span={8}>
                        <Input
                            placeholder="Search by Employee Code"
                            value={filters.searchCode}
                            onChange={handleSearchCodeChange}
                            onPressEnter={() => fetchData(1, pagination.pageSize)}
                            allowClear
                        />
                    </Col>

                    <Col span={8}>
                        <RangePicker
                            style={{ width: "100%" }}
                            value={filters.dateFrom && filters.dateTo ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)] : null}
                            onChange={handleDateRangeChange}
                        />
                    </Col>
                </Row>

                <Row gutter={16} style={{ marginTop: 15 }}>
                    <Col span={6}>
                        <Select
                            style={{ width: "100%" }}
                            placeholder="Select Organisation"
                            value={filters.selectedOrganisation || undefined}
                            onChange={handleOrgChange}
                            allowClear
                        >
                            {organisationList.map((org) => (
                                <Option key={org.id} value={org.id}>
                                    {org.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            placeholder="Select Department"
                            value={filters.selectedDepartment || undefined}
                            onChange={handleDeptChange}
                            disabled={!filters.selectedOrganisation}
                            allowClear
                        >
                            {departmentList.map((dept) => (
                                <Option key={dept.id} value={dept.id}>
                                    {dept.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                option?.children?.toLowerCase().includes(input.toLowerCase())
                            }
                            style={{ width: "100%" }}
                            placeholder="Select Designation"
                            value={filters.selectedDesignation || undefined}
                            onChange={handleDesigChange}
                            disabled={!filters.selectedDepartment}
                            allowClear
                        >
                            {designationList.map((des) => (
                                <Option key={des.id} value={des.id}>
                                    {des.name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col span={6}>
                        <Button
                            type="primary"
                            onClick={applyFilters}
                            style={{ marginRight: 8, background: '#0e497b', color: 'white' }}
                        >
                            Apply Filters
                        </Button>
                        <Button onClick={clearFilters}>
                            Clear
                        </Button>
                    </Col>
                </Row>

                <Row style={{ marginTop: 15 }} gutter={16} align="middle">
                    <Col>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            onClick={exportToExcel}
                            style={{ backgroundColor: "#0e497b" }}
                        >
                            Export to Excel
                        </Button>
                    </Col>
                    <Col>
                        {/* ✅ Custom Columns feature trigger */}
                        <Popover
                            content={columnPickerContent}
                            title="Customize Columns"
                            trigger="click"
                            open={columnPickerOpen}
                            onOpenChange={setColumnPickerOpen}
                            placement="bottomLeft"
                        >
                            <Button icon={<SettingOutlined />}>
                                Customize Columns
                            </Button>
                        </Popover>
                    </Col>
                </Row>
            </Card>

            {loading ? (
                <div style={{ textAlign: "center", padding: "50px" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Table
                    columns={columns}
                    dataSource={paginatedData}
                    rowKey="id"
                    bordered
                    pagination={{ ...pagination, total: statusFilteredData.length }}
                    onChange={handleTableChange}
                    expandable={{ expandedRowRender }}
                    scroll={{ x: "max-content" }}
                    rowClassName={(record) => {
                        const targetIdNum = parseInt(targetOtId, 10);
                        return record.id === targetIdNum ? "highlighted-row" : "";
                    }}
                    onRow={(record) => {
                        const targetIdNum = parseInt(targetOtId, 10);
                        return {
                            ref: record.id === targetIdNum ? highlightRowRef : null,
                        };
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            )}

            {/* Modal */}
            <Modal
                title={actionType === "approve" ? "Approve OT Request" : "Reject OT Request"}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setIsModalVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={submitLoading}
                        style={{
                            backgroundColor: actionType === "approve" ? "green" : "red",
                            color: "white",
                        }}
                        onClick={handleSubmit}
                    >
                        Submit
                    </Button>,
                ]}
            >
                <p>
                    <strong>Employee:</strong>{" "}
                    {selectedRecord?.employee?.first_name || selectedRecord?.employee?.last_name
                        ? `${selectedRecord?.employee?.first_name || ""} ${selectedRecord?.employee?.last_name || ""}`.trim()
                        : (selectedRecord?.employee_name || selectedRecord?.employee?.name || "—")}
                </p>
                <p>
                    <strong>Date:</strong> {selectedRecord?.attendance_date || "—"}
                </p>
                <p>
                    <strong>OT Hours:</strong> {selectedRecord?.approved_ot_hours || "—"}
                </p>
                <p>
                    <strong>OT Amount:</strong> {selectedRecord?.ot_amount ? `₹${selectedRecord.ot_amount}` : "—"}
                </p>

                <TextArea
                    rows={4}
                    placeholder={`Enter manager remarks for ${actionType === "approve" ? "approval" : "rejection"
                        }`}
                    name="remarks"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                />
            </Modal>

            <ToastContainer position="top-right" autoClose={3000} />
        </div>
    );
}